/**
 * AuthenticatedLayout — the Fluent v9 authenticated app shell (ADR-C).
 *
 * Two-shell architecture: this layout is used ONLY by authenticated routes
 * (/dashboard, /submissions, /admin). The public routes keep AppLayout.tsx
 * (SCSS-based) unchanged.
 *
 * Features:
 *   - NavDrawer: inline (pushes content) on desktop > 768px, overlay on mobile.
 *   - AppHeader at the top with hamburger toggle.
 *   - AppNav provides the sidebar items (Admin hidden for non-admins).
 *   - <Outlet /> for the routed page content.
 *   - Scroll-to-top on route change via window.scrollTo + useEffect on location.key.
 *   - Fixed header + sticky sidebar + window-level scroll (see layoutConstants).
 *
 * Uses makeStyles + tokens only. No new SCSS.
 */

import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  NavDrawer,
  NavDrawerBody,
  NavDrawerHeader,
  type OnNavItemSelectData,
} from '@fluentui/react-nav';
import {
  Button,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { AppHeader } from './AppHeader';
import { AppNav } from './AppNav';
import { HEADER_HEIGHT, INLINE_DRAWER_STICKY_STYLE } from './layoutConstants';
import uhealthLogo from '@/assets/uhealth-logo.svg';

// ---------------------------------------------------------------------------
// Responsive breakpoint
// ---------------------------------------------------------------------------

const MOBILE_BREAKPOINT = 768;
const isDesktop = () => window.innerWidth > MOBILE_BREAKPOINT;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    paddingTop: HEADER_HEIGHT,
  },
  body: {
    display: 'flex',
    flexGrow: 1,
    width: '100%',
    maxWidth: '1320px',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingTop: '24px',
    columnGap: '32px',
  },
  // Inline drawer (desktop): blends into the page background, no border.
  drawer: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderRightWidth: 0,
    borderRightStyle: 'none',
    flexShrink: 0,
    overflowY: 'auto',
    // NavDrawerBody has a 25px default top padding — zero it so nav items align
    // with the body row's shared 24px top padding.
    '& .fui-NavDrawerBody': { paddingTop: 0 },
  },
  // Mobile overlay drawer: solid background, safe-area insets.
  drawerOverlay: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: 'env(safe-area-inset-top)',
    paddingLeft: 'env(safe-area-inset-left)',
    '& .fui-NavDrawerBody': { paddingTop: tokens.spacingVerticalS },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
  },
  drawerLogo: {
    height: '32px',
    width: '36px',
    objectFit: 'cover',
    objectPosition: 'left',
  },
  drawerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    // Match the expanded header title colour (BrandLockup subtitle).
    color: tokens.colorNeutralForeground1,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'nowrap',
    // Never let the title force the drawer wider than its panel: shrink and
    // ellipsize instead. A too-wide title overflows and the browser
    // horizontally auto-scrolls the drawer, shearing the header + nav labels
    // off the left edge.
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  drawerClose: {
    marginLeft: 'auto',
  },
  content: {
    flexGrow: 1,
    minWidth: 0,
    // Flex column so a page can opt into filling the shell's full height
    // (e.g. the dashboard's Recent Activity feed) via flexGrow on its root.
    display: 'flex',
    flexDirection: 'column',
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AuthenticatedLayout() {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(isDesktop);
  const [drawerType, setDrawerType] = useState<'inline' | 'overlay'>(
    isDesktop() ? 'inline' : 'overlay'
  );

  // Update drawer type on window resize.
  useEffect(() => {
    const onResize = () => {
      const desktop = isDesktop();
      setDrawerType(desktop ? 'inline' : 'overlay');
      setOpen(desktop);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll to top on every route change. The page now scrolls at the window
  // level (not inside <main>), so scroll the window.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.key]);

  const handleNavItemSelect = (event: unknown, data: OnNavItemSelectData) => {
    // Modified clicks (ctrl/cmd/shift, middle button) → let the browser handle
    // them (open-in-new-tab). Only plain left-clicks are intercepted for SPA nav.
    const ev = event as React.MouseEvent;
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey || ev.button > 0) {
      return;
    }
    ev.preventDefault?.();
    if (typeof data.value === 'string') {
      navigate(data.value);
    }
    if (drawerType === 'overlay') setOpen(false);
  };

  return (
    <div className={styles.root}>
      <AppHeader onToggleSidebar={() => setOpen((o) => !o)} />

      <div className={styles.body}>
        <NavDrawer
          open={open}
          type={drawerType}
          className={mergeClasses(
            styles.drawer,
            drawerType === 'overlay' && styles.drawerOverlay
          )}
          style={drawerType === 'inline' ? INLINE_DRAWER_STICKY_STYLE : undefined}
          selectedValue={location.pathname}
          onNavItemSelect={handleNavItemSelect}
          onOpenChange={(_e, data) => setOpen(data.open)}
        >
          {drawerType === 'overlay' && (
            <NavDrawerHeader>
              <div className={styles.drawerHeader}>
                <img
                  src={uhealthLogo}
                  alt="University of Utah Health"
                  className={styles.drawerLogo}
                />
                <span className={styles.drawerTitle}>AI Intake</span>
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  aria-label="Close navigation"
                  className={styles.drawerClose}
                  onClick={() => setOpen(false)}
                />
              </div>
            </NavDrawerHeader>
          )}
          <NavDrawerBody>
            <AppNav />
          </NavDrawerBody>
        </NavDrawer>

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AuthenticatedLayout;
